import React, { useState, useEffect } from 'react';
import { Save, CheckCircle } from 'lucide-react';
import { InstagramAccount } from '../../types';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';

interface WelcomeDMProps {
  account: InstagramAccount;
  isDarkMode: boolean;
  language: string;
  onAccountUpdated?: () => Promise<void>;
}

export function WelcomeDM({ account, isDarkMode, language, onAccountUpdated }: WelcomeDMProps) {
  const [message, setMessage] = useState(account.welcome_message || '');
  const [showSaved, setShowSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const t = translations[language as keyof typeof translations];

  // Update message when account changes
  useEffect(() => {
    setMessage(account.welcome_message || '');
  }, [account.welcome_message]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      // Update the account in the database first
      const { error } = await supabase
        .from('instagram_accounts')
        .update({ welcome_message: message })
        .eq('id', account.id);

      if (error) throw error;

      // Send webhook notification to Make
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/welcome-dm-webhook`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({
            username: account.username,
            welcome_message: message,
            timestamp: new Date().toISOString(),
            account_id: account.id
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Webhook response not ok:', response.status, response.statusText, errorData);
        } else {
          console.log('Welcome message webhook sent successfully');
        }
      } catch (webhookError) {
        console.error('Failed to send webhook notification:', webhookError);
      }

      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);

      // Call the onAccountUpdated callback to refresh the account data
      if (onAccountUpdated) {
        await onAccountUpdated();
      }
    } catch (err) {
      console.error('Error saving welcome message:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`p-6 rounded-lg border ${
      isDarkMode 
        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
        : 'bg-white border-[#e5e7eb]'
    }`}>
      <h2 className={`text-lg font-semibold mb-2 ${
        isDarkMode ? 'text-gray-200' : 'text-gray-900'
      }`}>{t.welcome_dm}</h2>

      <p className={`text-sm mb-4 whitespace-pre-line ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        {t.welcome_dm_description}
      </p>
      
      <div className="relative">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t.welcome_dm_placeholder}
          rows={6}
          className={`w-full rounded-lg px-4 py-3 text-[13px] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none ${
            isDarkMode 
              ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]' 
              : 'bg-gray-50 text-gray-900 border-gray-200'
          }`}
        />
        
        <div className="flex items-center justify-end gap-2 mt-3">
          {showSaved && (
            <div className="flex items-center gap-1 text-[13px] text-emerald-600">
              <CheckCircle size={14} strokeWidth={2.5} />
              <span className="font-medium">{t.welcome_dm_saved}</span>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex items-center gap-1.5 bg-orange-500 text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} strokeWidth={2.5} />
            {isLoading ? t.please_wait : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}